import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { zodResolver } from '@hookform/resolvers/zod';
import { Box, Typography } from '@mui/material';
import dayjs from 'dayjs';
import duration from 'dayjs/plugin/duration';
import { z } from 'zod';

import { useResendCode, useVerifyCode } from '@/features/auth';
import { FormError } from '@/shared/ui';
import { Button } from '@/shared/ui/lib/Button';
import { Logo } from '@/shared/ui/Logo';
import { RouterLink } from '@/shared/ui/RouterLink';
import { ControlledTextField } from '@/shared/ui/TextField';
import { CustomTypography } from '@/shared/ui/Typography';

import { StyledBox } from './styles';

dayjs.extend(duration);

const createCodeSchema = (t: (key: string) => string) =>
  z.object({
    code: z.string().min(1, t('common:errors.required')),
  });

export const CodeVerification = () => {
  const { t } = useTranslation(['auth']);

  const location = useLocation();
  const [countdown, setCountdown] = useState(60);
  const [errorData, setErrorData] = useState<string | null>(null);

  const { verifyCode, isPending: isVerifying } = useVerifyCode();
  const { resendCode, isPending: isResending } = useResendCode();

  const {
    control,
    handleSubmit,
    formState: { isValid },
  } = useForm({
    resolver: zodResolver(createCodeSchema(t)),
    defaultValues: {
      code: '',
    },
    mode: 'onChange',
  });

  const isResendDisabled = countdown > 0;
  const formattedCountdown = dayjs
    .duration(countdown, 'seconds')
    .format('mm:ss');

  const startCountdown = () => {
    setCountdown(60);
  };

  const resendVerificationCode = async () => {
    if (isResendDisabled) return;

    try {
      await resendCode({ token: location.state?.token });
      startCountdown();
    } catch (error: any) {
      setErrorData(error.response?.data?.details || t('common:errors.generic'));
    }
  };

  const onSubmit = async (data: { code: string }) => {
    try {
      await verifyCode({
        token: location.state?.token,
        code: data.code.trim(),
      });
    } catch (error: any) {
      setErrorData(
        error.response?.data?.details || t('common:errors.invalidCode')
      );
    }
  };

  useEffect(() => {
    startCountdown();
  }, []);

  useEffect(() => {
    let interval: NodeJS.Timeout;

    if (countdown > 0) {
      interval = setInterval(() => {
        setCountdown((prev) => prev - 1);
      }, 1000);
    }

    return () => clearInterval(interval);
  }, [countdown]);

  return (
    <StyledBox>
      <Logo />
      <Box
        sx={{ width: '100%' }}
        component="form"
        onSubmit={handleSubmit(onSubmit)}
      >
        <CustomTypography
          variant="h6"
          sx={{ mb: 3, mt: 2, textAlign: 'center' }}
        >
          {t('auth:codeVerification.title')}
        </CustomTypography>

        <Box>
          <Typography variant="body1">
            {t('auth:codeVerification.sendedTo')}{' '}
            <Typography component="span" fontWeight="bold">
              {location.state?.email}
            </Typography>
          </Typography>
        </Box>

        <Box sx={{ mb: 1, mt: 2 }}>
          <ControlledTextField
            name="code"
            control={control}
            // label={t('common:inputs.code')}
            type="text"
            placeholder={t('common:inputs.placeholders.code')}
            disabled={isVerifying}
            autoFocus
          />
        </Box>

        {errorData && <FormError message={errorData} />}

        <Box>
          <Button
            type="submit"
            variant="contained"
            color="primary"
            disabled={!isValid || isVerifying}
            fullWidth
            sx={{
              textTransform: 'none',
              display: 'block',
              margin: '20px auto',
              // width: '180px',
            }}
            loading={isVerifying}
          >
            {t('common:buttons.continue')}
          </Button>

          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            width="100%"
          >
            <RouterLink to="/auth/login" underline="hover" variant="body2">
              {t('auth:common:backToLogin')}
            </RouterLink>

            {isResendDisabled ? (
              <Typography color="text.secondary">
                {t('auth:codeVerification.resendCodeIn')} {formattedCountdown}
              </Typography>
            ) : (
              <Button
                onClick={resendVerificationCode}
                variant="text"
                size="small"
                loading={isResending}
              >
                {t('auth:codeVerification.resendCode')}
              </Button>
            )}
          </Box>
        </Box>
      </Box>
    </StyledBox>
  );
};
